const BASE_URL = import.meta.env.VITE_API_URL ?? "";

// HIPAA transmission security requirement: API calls carry PHI, so they must never
// be sent over plaintext HTTP. Fail loudly at load time if VITE_API_URL is misconfigured,
// rather than silently leaking PHI over HTTP. Localhost/127.0.0.1 are exempt for local dev.
if (BASE_URL && !BASE_URL.startsWith("https://") && !BASE_URL.startsWith("http://localhost") && !BASE_URL.startsWith("http://127.0.0.1")) {
  throw new Error(
    `VITE_API_URL must use https:// in production. Got: ${BASE_URL}. Localhost/127.0.0.1 are exempt for local development.`
  );
}

export type Portal = "patient" | "admin" | "caregiver";

interface AuthBinding {
  getToken: () => string | null;
  /** Attempts a silent token refresh (via the portal's refresh-cookie endpoint). Resolves to whether it succeeded. */
  refresh: () => Promise<boolean>;
  clear: () => void;
}

const bindings: Partial<Record<Portal, AuthBinding>> = {};

/** Called once by each portal's auth store so apiClient can attach/refresh its token without importing the store directly (would create a circular import). */
export function registerAuthBinding(portal: Portal, binding: AuthBinding) {
  bindings[portal] = binding;
}

export interface ApiFetchOptions extends Omit<RequestInit, "headers"> {
  /** Which portal's token to attach and refresh-on-401 with. Omit for endpoints that need neither (e.g. login/signup). */
  portal?: Portal;
  /** Skip token attachment and the refresh-on-401 retry entirely — for the auth endpoints themselves (login, refresh, logout, signup, OTP). */
  skipAuth?: boolean;
  headers?: Record<string, string>;
  /** Overrides DEFAULT_TIMEOUT_MS for this call. Ignored if `signal` is already provided — that caller owns its own cancellation. */
  timeoutMs?: number;
}

// Without this, a stalled request (backend hung on a slow downstream call, a dropped
// connection that never resets) leaves the fetch() promise pending forever — callers
// relying on this resolving/rejecting are left stuck on a loading state indefinitely,
// with nothing to catch and no way to retry. 30s comfortably covers a normal slow
// response; callers expecting a genuinely long-running operation can override via
// `timeoutMs` (or supply their own `signal` to opt out of this entirely).
const DEFAULT_TIMEOUT_MS = 30_000;

// A global exception handler on the backend reshapes every error response — including
// FastAPI's default {detail} ones — into this single shape, for both portals.
export interface ApiError {
  code: string;
  message: string;
  /** Present only on TOO_MANY_ATTEMPTS-style rate-limit errors. */
  retryAfterSeconds?: number;
  /** Present only on EMAIL_NOT_VERIFIED-style errors that reference an existing pending OTP. */
  otpExpiresInSeconds?: number;
}

export interface ApiFetchResult<T = unknown> {
  ok: boolean;
  status: number;
  data: T | null;
  error: ApiError | null;
  networkError: boolean;
}

async function parseApiError(response: Response): Promise<ApiError> {
  try {
    const data = await response.json();
    return {
      code: data?.code ?? "UNKNOWN_ERROR",
      message: data?.message ?? "Something went wrong.",
      retryAfterSeconds: data?.retryAfterSeconds,
      otpExpiresInSeconds: data?.otpExpiresInSeconds,
    };
  } catch {
    return { code: "UNKNOWN_ERROR", message: "Something went wrong." };
  }
}

async function rawFetch(path: string, options: ApiFetchOptions): Promise<Response> {
  const { portal, skipAuth, headers, body, timeoutMs, ...rest } = options;
  const finalHeaders: Record<string, string> = { ...headers };

  if (!(body instanceof FormData) && !finalHeaders["Content-Type"]) {
    finalHeaders["Content-Type"] = "application/json";
  }

  if (portal && !skipAuth) {
    const token = bindings[portal]?.getToken();
    if (token) finalHeaders.Authorization = `Bearer ${token}`;
  }

  // ngrok's free tier shows an HTML interstitial warning page to requests that look
  // browser-originated, which has no CORS headers and breaks every fetch() call through
  // the tunnel. This header tells ngrok to skip it and proxy straight to the backend.
  finalHeaders["ngrok-skip-browser-warning"] = "true";

  // `path` is normally relative (prefixed with BASE_URL below), but some callers (e.g.
  // useProxiedImage, given a fully-qualified avatarUrl straight from the backend) already
  // have an absolute URL — don't double-prefix those.
  const url = /^https?:\/\//.test(path) ? path : `${BASE_URL}${path}`;

  // If the caller already supplied its own `signal`, it owns cancellation —
  // don't layer a second one on top.
  let signal = rest.signal;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  if (!signal) {
    const controller = new AbortController();
    timeoutId = setTimeout(() => controller.abort(), timeoutMs ?? DEFAULT_TIMEOUT_MS);
    signal = controller.signal;
  }

  try {
    return await fetch(url, {
      ...rest,
      body,
      headers: finalHeaders,
      credentials: "include",
      signal,
    });
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

/**
 * Shared fetch wrapper for every API module (patient + admin). Attaches the bearer
 * token for protected calls and, on a single 401, attempts one silent refresh and
 * retries once before giving up and clearing the session. Errors are parsed once,
 * here, into the single {code, message} shape the backend's global exception
 * handler guarantees app-wide — callers never branch on per-portal error shapes.
 */
export async function apiFetch<T = unknown>(path: string, options: ApiFetchOptions = {}): Promise<ApiFetchResult<T>> {
  try {
    let res = await rawFetch(path, options);

    if (res.status === 401 && options.portal && !options.skipAuth) {
      const binding = bindings[options.portal];
      const refreshed = binding ? await binding.refresh() : false;
      if (refreshed) res = await rawFetch(path, options);
      if (res.status === 401) binding?.clear();
    }

    if (!res.ok) {
      const error = await parseApiError(res);
      return { ok: false, status: res.status, data: null, error, networkError: false };
    }

    const data = await res.json().catch(() => null);
    return { ok: true, status: res.status, data: data as T, error: null, networkError: false };
  } catch (err) {
    // A caller-triggered abort (component unmounted, explicit cancel) isn't a
    // real failure, but our own DEFAULT_TIMEOUT_MS firing is — distinguished
    // here so callers can show "this is taking too long" instead of the
    // generic connectivity message.
    const timedOut = err instanceof DOMException && err.name === "AbortError";
    return {
      ok: false,
      status: 0,
      data: null,
      error: timedOut
        ? { code: "TIMEOUT", message: "The request took too long. Please try again." }
        : { code: "NETWORK_ERROR", message: "Unable to connect. Please check your internet connection." },
      networkError: true,
    };
  }
}

/**
 * Like apiFetch, but for binary responses (images, etc.) that the caller reads
 * itself (e.g. via .blob()) instead of JSON. Same bearer-token attachment and
 * one-shot refresh-on-401 retry as apiFetch — just returns the raw Response
 * instead of parsing a body.
 */
export async function apiFetchRaw(path: string, options: ApiFetchOptions = {}): Promise<Response> {
  let res = await rawFetch(path, options);

  if (res.status === 401 && options.portal && !options.skipAuth) {
    const binding = bindings[options.portal];
    const refreshed = binding ? await binding.refresh() : false;
    if (refreshed) res = await rawFetch(path, options);
    if (res.status === 401) binding?.clear();
  }

  return res;
}
